import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { depenseService, stockService } from '../../services/api';
import {
  Depense,
  CreateDepenseData,
  CategorieDepense,
  Fournisseur,
  PrioriteDepense,
  NatureDepense,
} from '../../types';

interface DepenseFormProps {
  depense?: Depense | null;
  onSubmit: (data: CreateDepenseData) => void;
  onClose: () => void;
  isLoading?: boolean;
}

const DepenseForm: React.FC<DepenseFormProps> = ({
  depense,
  onSubmit,
  onClose,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreateDepenseData>({
    categorie: depense?.categorie || 0,
    description: depense?.description || '',
    montant: depense?.montant || 0,
    date_depense: depense?.date_depense || new Date().toISOString().split('T')[0],
    date_echeance: depense?.date_echeance || '',
    priorite: depense?.priorite || 'normale',
    fournisseur: depense?.fournisseur || undefined,
    reference_facture: depense?.reference_facture || '',
    notes: depense?.notes || '',
    est_recurrente: depense?.est_recurrente || false,
    frequence_recurrence: depense?.frequence_recurrence || undefined,
    nature: depense?.nature || 'journaliere',
    impacte_caisse: depense?.impacte_caisse ?? true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Catégories proposées à la saisie : actives uniquement
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-depenses', 'actives'],
    queryFn: () => depenseService.getCategoriesActives(),
  });

  // Fetch fournisseurs
  const { data: fournisseursData } = useQuery({
    queryKey: ['fournisseurs'],
    queryFn: () => stockService.getFournisseurs(),
  });

  // Fetch priorités
  const { data: prioritesData } = useQuery({
    queryKey: ['priorites-depenses'],
    queryFn: () => depenseService.getPriorites(),
  });

  const categoriesActives = categoriesData?.data?.results || [];
  const fournisseurs = fournisseursData?.data?.results || [];
  const priorites = prioritesData?.data || [];

  /* Si on modifie une dépense dont la catégorie a été désactivée entre-temps,
     on la conserve dans la liste (sinon le champ perdrait sa valeur). */
  const categorieDepenseDesactivee =
    depense?.categorie && !categoriesActives.some((c: CategorieDepense) => c.id === depense.categorie)
      ? depense.categorie_details
      : null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.categorie) {
      newErrors.categorie = 'La catégorie est requise';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    if (!formData.montant || formData.montant <= 0) {
      newErrors.montant = 'Le montant doit être supérieur à 0';
    }
    if (!formData.date_depense) {
      newErrors.date_depense = 'La date est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData: CreateDepenseData = {
        ...formData,
        fournisseur: formData.fournisseur || undefined,
        date_echeance: formData.date_echeance || undefined,
        frequence_recurrence: formData.est_recurrente ? formData.frequence_recurrence : undefined,
      };
      onSubmit(submitData);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {depense ? 'Modifier la dépense' : 'Nouvelle dépense'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Catégorie *
            </label>
            <select
              name="categorie"
              value={formData.categorie}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700 ${
                errors.categorie ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner une catégorie</option>
              {categorieDepenseDesactivee && (
                <option value={categorieDepenseDesactivee.id}>
                  {categorieDepenseDesactivee.nom} (désactivée)
                </option>
              )}
              {categoriesActives.map((cat: CategorieDepense) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nom}
                </option>
              ))}
            </select>
            {errors.categorie && (
              <p className="text-red-500 text-xs mt-1">{errors.categorie}</p>
            )}
            {categorieDepenseDesactivee && (
              <p className="text-amber-600 text-xs mt-1">
                La catégorie d'origine est désactivée : conservez-la ou choisissez-en une active.
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700 ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Description de la dépense"
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1">{errors.description}</p>
            )}
          </div>

          {/* Montant et Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant (GNF) *
              </label>
              <input
                type="number"
                name="montant"
                value={formData.montant}
                onChange={handleChange}
                min="0"
                step="1"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700 ${
                  errors.montant ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0"
              />
              {errors.montant && (
                <p className="text-red-500 text-xs mt-1">{errors.montant}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de dépense *
              </label>
              <input
                type="date"
                name="date_depense"
                value={formData.date_depense}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700 ${
                  errors.date_depense ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.date_depense && (
                <p className="text-red-500 text-xs mt-1">{errors.date_depense}</p>
              )}
            </div>
          </div>

          {/* Date échéance et Priorité */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date d'échéance
              </label>
              <input
                type="date"
                name="date_echeance"
                value={formData.date_echeance || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priorité
              </label>
              <select
                name="priorite"
                value={formData.priorite}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
              >
                {priorites.map((p: { value: string; label: string }) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Nature de la dépense : impact ou non sur la caisse journalière */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3">
            <p className="text-sm font-medium text-gray-700">Nature de la dépense</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {([
                {
                  value: 'journaliere' as NatureDepense,
                  titre: 'Journalière',
                  detail: 'Décaissée de la caisse du jour (achats courants, petites fournitures).',
                },
                {
                  value: 'mensuelle' as NatureDepense,
                  titre: 'Mensuelle',
                  detail: 'Réglée hors caisse journalière : salaires, factures fournisseurs.',
                },
              ]).map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                    formData.nature === option.value
                      ? 'border-teal-500 bg-white ring-1 ring-teal-500'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="nature"
                    value={option.value}
                    checked={formData.nature === option.value}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nature: option.value,
                        // une mensuelle sort du circuit caisse par défaut
                        impacte_caisse: option.value === 'journaliere',
                      }))
                    }
                    className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">{option.titre}</span>
                    <span className="block text-xs text-gray-500">{option.detail}</span>
                  </span>
                </label>
              ))}
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="impacte_caisse"
                checked={formData.impacte_caisse ?? true}
                onChange={handleChange}
                className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <span>
                <span className="block text-sm text-gray-700">
                  Enregistrer le décaissement dans la caisse du jour
                </span>
                <span className="block text-xs text-gray-500">
                  {formData.impacte_caisse
                    ? "La dépense apparaîtra dans le folio de caisse ou Mobile Money du jour de paiement."
                    : "La dépense n'apparaîtra pas dans le folio journalier ; elle reste comptabilisée normalement."}
                </span>
              </span>
            </label>
          </div>

          {/* Fournisseur et Référence facture */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fournisseur
              </label>
              <select
                name="fournisseur"
                value={formData.fournisseur || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
              >
                <option value="">Aucun fournisseur</option>
                {fournisseurs.map((f: Fournisseur) => (
                  <option key={f.id} value={f.id}>
                    {f.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Référence facture
              </label>
              <input
                type="text"
                name="reference_facture"
                value={formData.reference_facture || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
                placeholder="N° de facture fournisseur"
              />
            </div>
          </div>

          {/* Dépense récurrente */}
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="est_recurrente"
                checked={formData.est_recurrente}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Dépense récurrente</span>
            </label>

            {formData.est_recurrente && (
              <select
                name="frequence_recurrence"
                value={formData.frequence_recurrence || ''}
                onChange={handleChange}
                className="px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
              >
                <option value="">Fréquence</option>
                <option value="mensuelle">Mensuelle</option>
                <option value="trimestrielle">Trimestrielle</option>
                <option value="annuelle">Annuelle</option>
              </select>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
              placeholder="Notes ou commentaires..."
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50"
            >
              {isLoading ? 'Enregistrement...' : depense ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepenseForm;
